import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Divider, Form, Input, Popconfirm, Select, Spin, message as antMessage } from "antd";
import { serverFetch } from "@core/server";
import dayjs from "dayjs";
import { fetchSprSotrList } from "../sprs/sotr/spr-sotr-api";
import { fetchSprPredList } from "../sprs/pred/spr-pred-api";

const errMsg = (e) => e?.message || "Ошибка";

const fmtDate = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY HH:mm") : null;
};

const mapPredOptions = (data, extra) => {
  const rows = Array.isArray(data) ? data : data?.items || data?.Items || [];
  const opts = rows
    .map((row) => {
      const value = Number(row.idPred ?? row.IdPred);
      if (!Number.isFinite(value) || value <= 0) return null;
      return { value, label: row.name || row.Name || `#${value}` };
    })
    .filter(Boolean);
  if (extra?.value && !opts.some((o) => o.value === extra.value)) opts.unshift(extra);
  return opts;
};

const DialogMain = ({ dialog: dialogProp, onReload, onClose }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [agentsList, setAgentsList] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [predOptions, setPredOptions] = useState([]);
  const [predLoading, setPredLoading] = useState(false);
  const predTimerRef = useRef(null);

  const editBlocked = Boolean(dialogProp?.deleted);
  const idPred = Form.useWatch("idPred", form) ?? dialogProp?.idPred ?? null;

  useEffect(() => {
    setLoadingUsers(true);
    fetchSprSotrList()
      .then((list) => setUsersList(Array.isArray(list) ? list : []))
      .catch(() => setUsersList([]))
      .finally(() => setLoadingUsers(false));
    setLoadingAgents(true);
    serverFetch("Agent")
      .then((list) => setAgentsList(Array.isArray(list) ? list : []))
      .catch(() => setAgentsList([]))
      .finally(() => setLoadingAgents(false));
  }, []);

  useEffect(() => {
    if (!dialogProp) return;
    form.setFieldsValue({
      name: dialogProp.name ?? "",
      enteredName: dialogProp.enteredName ?? "",
      enteredPhone: dialogProp.enteredPhone ?? "",
      enteredEmail: dialogProp.enteredEmail ?? "",
      idSotr: dialogProp.idSotr ?? null,
      agentId: dialogProp.agentId ?? null,
      idPred: dialogProp.idPred ?? null,
      note: dialogProp.note ?? ""
    });
    if (dialogProp.idPred) {
      setPredOptions((prev) =>
        mapPredOptions([], {
          value: dialogProp.idPred,
          label: dialogProp.predName || `#${dialogProp.idPred}`
        }).concat(prev.filter((o) => o.value !== dialogProp.idPred))
      );
    }
  }, [dialogProp, form]);

  const loadPreds = (query = "") => {
    clearTimeout(predTimerRef.current);
    predTimerRef.current = setTimeout(() => {
      setPredLoading(true);
      fetchSprPredList({ q: query, take: 50 })
        .then((data) => {
          setPredOptions(
            mapPredOptions(data, dialogProp?.idPred
              ? { value: dialogProp.idPred, label: dialogProp.predName || `#${dialogProp.idPred}` }
              : null)
          );
        })
        .catch(() => {})
        .finally(() => setPredLoading(false));
    }, 250);
  };

  useEffect(() => {
    loadPreds("");
    return () => clearTimeout(predTimerRef.current);
  }, []);

  const userOptions = useMemo(
    () =>
      usersList.map((u) => {
        const id = u.idSotr ?? u.IdSotr;
        return { value: id, label: u.fullName || u.name || `#${id}` };
      }),
    [usersList]
  );

  const agentOptions = useMemo(
    () =>
      agentsList
        .filter((a) => a.active || a.id === dialogProp?.agentId)
        .map((a) => ({
          value: a.id,
          label: a.active ? a.name || `#${a.id}` : `${a.name || `#${a.id}`} (неактивен)`
        })),
    [agentsList, dialogProp?.agentId]
  );

  const onFinish = (values) => {
    if (!dialogProp?.id) return;
    setSaving(true);
    const persistEntered = values.idPred == null;
    serverFetch("Message/Dialog", {
      method: "PUT",
      bodyData: {
        ...dialogProp,
        name: values.name ?? "",
        enteredName: persistEntered ? values.enteredName ?? "" : dialogProp.enteredName ?? "",
        enteredPhone: persistEntered ? values.enteredPhone ?? "" : dialogProp.enteredPhone ?? "",
        enteredEmail: persistEntered ? values.enteredEmail ?? "" : dialogProp.enteredEmail ?? "",
        idSotr: values.idSotr ?? null,
        agentId: values.agentId ?? null,
        idPred: values.idPred ?? null,
        note: values.note ?? ""
      }
    })
      .then(() => {
        antMessage.success("Сохранено");
        onReload?.();
      })
      .catch((e) => antMessage.error(errMsg(e)))
      .finally(() => setSaving(false));
  };

  const onDeleteDialog = () => {
    if (!dialogProp?.id) return;
    setActionLoading("delete");
    serverFetch(`Message/DialogDelete/${dialogProp.id}`, { method: "DELETE" })
      .then(() => {
        antMessage.success("Диалог удалён");
        onClose?.();
      })
      .catch((e) => antMessage.error(errMsg(e)))
      .finally(() => setActionLoading(null));
  };

  const onUndeleteDialog = () => {
    if (!dialogProp?.id) return;
    setActionLoading("undelete");
    serverFetch(`Message/DialogUndelete/${dialogProp.id}`, { method: "DELETE" })
      .then(() => {
        antMessage.success("Диалог восстановлен");
        onReload?.();
      })
      .catch((e) => antMessage.error(errMsg(e)))
      .finally(() => setActionLoading(null));
  };

  if (!dialogProp) return null;

  return (
    <div className="dialog-detail">
      <div className="dialog-detail-contact">
        <Spin spinning={loadingUsers || loadingAgents}>
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ flex: "130px" }}
            wrapperCol={{ flex: "1" }}
            colon={false}
            onFinish={onFinish}
            requiredMark={false}
            className="dialog-detail-contact-form"
          >
            <Form.Item name="name" label="Наименование">
              <Input placeholder="Краткое название обращения" disabled={editBlocked} />
            </Form.Item>
            <Form.Item name="idPred" label="Контрагент">
              <Select
                allowClear
                showSearch
                filterOption={false}
                placeholder="Выберите контрагента"
                options={predOptions}
                loading={predLoading}
                disabled={editBlocked}
                onSearch={loadPreds}
                onDropdownVisibleChange={(open) => open && loadPreds("")}
              />
            </Form.Item>
            <Form.Item name="enteredName" label="Имя">
              <Input
                placeholder="ФИО"
                readOnly={idPred != null || editBlocked}
                disabled={editBlocked}
                className={idPred != null || editBlocked ? "dialog-detail-readonly-input" : undefined}
              />
            </Form.Item>
            <Form.Item name="enteredPhone" label="Телефон">
              <Input
                placeholder="+7 …"
                readOnly={idPred != null || editBlocked}
                disabled={editBlocked}
                className={idPred != null || editBlocked ? "dialog-detail-readonly-input" : undefined}
              />
            </Form.Item>
            <Form.Item name="enteredEmail" label="Email">
              <Input
                placeholder="email"
                readOnly={idPred != null || editBlocked}
                disabled={editBlocked}
                className={idPred != null || editBlocked ? "dialog-detail-readonly-input" : undefined}
              />
            </Form.Item>
            <Form.Item name="idSotr" label="Менеджер">
              <Select
                allowClear
                placeholder="Не назначен"
                options={userOptions}
                optionFilterProp="label"
                showSearch
                disabled={editBlocked}
              />
            </Form.Item>
            <Form.Item name="agentId" label="Агент">
              <Select
                allowClear
                placeholder="Не указан"
                options={agentOptions}
                optionFilterProp="label"
                showSearch
                disabled={editBlocked}
              />
            </Form.Item>
            <Form.Item name="note" label="Примечание">
              <Input.TextArea
                placeholder="Многострочное примечание"
                autoSize={{ minRows: 3, maxRows: 8 }}
                disabled={editBlocked}
              />
            </Form.Item>
            <Divider className="dialog-detail-divider-compact" />
            <Form.Item label="#" className="dialog-detail-readonly-row">
              <Input readOnly className="dialog-detail-readonly-input" value={String(dialogProp.id ?? "")} />
            </Form.Item>
            <Form.Item label="Активность">
              <Input
                readOnly
                className="dialog-detail-readonly-input"
                value={`${fmtDate(dialogProp.createdOn) ?? "—"} - ${fmtDate(dialogProp.lastUpdated) ?? "—"}`}
              />
            </Form.Item>
            <Form.Item label="Канал">
              <Input
                readOnly
                className="dialog-detail-readonly-input"
                value={
                  [dialogProp.channelName, dialogProp.externalId]
                    .map((x) => (x == null ? "" : String(x).trim()))
                    .filter(Boolean)
                    .join(" ") || "—"
                }
              />
            </Form.Item>
            <div className="dialog-detail-submit-wrap">
              <Button type="primary" htmlType="submit" loading={saving} disabled={editBlocked}>
                Сохранить
              </Button>
              {dialogProp.deleted ? (
                <Popconfirm title="Подтверждение" description="Восстановить диалог?" onConfirm={onUndeleteDialog}>
                  <Button loading={actionLoading === "undelete"}>Восстановить</Button>
                </Popconfirm>
              ) : (
                <Popconfirm title="Подтверждение" description="Удалить диалог?" onConfirm={onDeleteDialog}>
                  <Button danger loading={actionLoading === "delete"}>
                    Удалить
                  </Button>
                </Popconfirm>
              )}
            </div>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default DialogMain;
